import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Lock, AlertTriangle } from 'lucide-react';

const ForcePasswordChange = () => {
  const { changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
    } catch (err) {
      setError(err.response?.data?.detail || 'Password change failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="force-password-change">
      <div className="w-full max-w-md bg-white rounded-sm border border-slate-200 p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Password Change Required</h2>
            <p className="text-sm text-slate-500">You must change your password before continuing</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Current Password</label>
            <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required data-testid="current-password-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">New Password</label>
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required data-testid="new-password-input" />
            <p className="text-xs text-slate-400 mt-1">Min 8 characters, uppercase, lowercase, digit</p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Confirm New Password</label>
            <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="confirm-password-input" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="password-change-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-sm" data-testid="change-password-submit">
            <Lock className="w-4 h-4 mr-2" />{loading ? 'Changing...' : 'Change Password'}
          </Button>
        </form>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" style={{ width: '32px', height: '32px' }} />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (user === false) {
    return <Navigate to="/login" replace />;
  }

  if (user?.force_password_change) {
    return <ForcePasswordChange />;
  }

  return children;
};

export default ProtectedRoute;
